import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Instagram } from 'lucide-react';
import { CreatorVideoCard } from '@/components/home/CreatorVideos';
import { Button } from '@/components/ui/button';
import { CREATOR_VIDEOS } from '@/config/creatorVideos';
import { Reveal, Eyebrow, H2 } from './shared';

const HOME_EXCLUDED_VIDEO_IDS = new Set(['maos-pes', 'maternidade']);
const HOME_VIDEOS = CREATOR_VIDEOS.filter((video) => !HOME_EXCLUDED_VIDEO_IDS.has(video.id));

/** UGC em carrossel 9:16. Crédito da creator + selo "Parceria" sempre visíveis. */
export const CreatorsV2 = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const slides = [...HOME_VIDEOS, ...HOME_VIDEOS];

  useEffect(() => {
    if (reducedMotion) return;
    const timer = window.setInterval(() => {
      const viewport = viewportRef.current;
      if (viewport && !pausedRef.current) {
        viewport.scrollLeft += 1;
        const halfway = viewport.scrollWidth / 2;
        if (viewport.scrollLeft >= halfway) viewport.scrollLeft -= halfway;
      }
    }, 64);
    return () => window.clearInterval(timer);
  }, [reducedMotion]);

  const scrollBy = useCallback((direction: -1 | 1) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollBy({ left: direction * viewport.clientWidth * 0.7, behavior: 'smooth' });
  }, []);

  if (HOME_VIDEOS.length === 0) return null;
  return (
    <Reveal as="section" className="bg-background text-v2-ink py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-end justify-between gap-4">
          <div>
            <Eyebrow>Parcerias com creators</Eyebrow>
            <H2>Quem usa, mostra</H2>
          </div>
          <div className="hidden gap-2 md:flex">
            <Button variant="outline" size="icon" className="h-12 w-12 rounded-full" onClick={() => scrollBy(-1)} aria-label="Vídeos anteriores">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button variant="outline" size="icon" className="h-12 w-12 rounded-full" onClick={() => scrollBy(1)} aria-label="Próximos vídeos">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="mt-10 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-roledescription="carrossel"
          aria-label="Vídeos de creators"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = playingKey !== null; }}
          onPointerDown={() => { pausedRef.current = true; }}
          onPointerUp={() => { pausedRef.current = playingKey !== null; }}
          onPointerCancel={() => { pausedRef.current = playingKey !== null; }}
          onFocusCapture={() => { pausedRef.current = true; }}
          onBlurCapture={() => { pausedRef.current = playingKey !== null; }}
        >
          <div className="flex gap-3 md:gap-4">
            {slides.map((video, index) => {
              const slideKey = `${video.id}-${index}`;
              return (
                <div
                  key={slideKey}
                  className="relative w-[calc(50%-6px)] flex-none min-w-0 md:w-[calc(25%-12px)]"
                  role="group"
                  aria-label={`${video.creator}: ${video.legenda}`}
                >
                  <span className="absolute top-2 left-2 z-10 text-sm font-semibold uppercase px-2 py-0.5 rounded-full bg-background/90 text-v2-ink pointer-events-none">
                    Parceria
                  </span>
                  <CreatorVideoCard
                    video={video}
                    isPlaying={playingKey === slideKey}
                    onPlay={() => {
                      setPlayingKey((current) => {
                        const next = current === slideKey ? null : slideKey;
                        pausedRef.current = next !== null;
                        return next;
                      });
                    }}
                    className="w-full min-w-0"
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="text-center mt-8">
          <a
            href="https://www.instagram.com/nat.hof.care/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 min-h-12 px-6 rounded-full border border-v2-ink/25 font-semibold hover:bg-v2-ink/5"
          >
            <Instagram className="w-4 h-4" /> Ver no Instagram @nat.hof.care
          </a>
        </div>
      </div>
    </Reveal>
  );
};
